import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import FormModal from "components/modals/FormModal";
import {InputText} from "primereact/inputtext";
import {
    useCreateKpiTargetMutation,
    useGetKpiTargetQuery,
    useListKpiTargetsQuery,
    useUpdateKpiTargetMutation
} from "../../apis/kpiApi";
import {Dropdown} from "primereact/dropdown";
import _ from "lodash";
import {useSelector} from "react-redux";
import {Calendar} from "primereact/calendar";
import {Divider} from "primereact/divider";
import KPITargetAnnual from "./KPITargetAnnual";
import {DateTime} from "luxon";
import valueTypes from "data/kpi-value-types.json";
import goalTypes from "data/kpi-goal-types.json";
import levels from "data/kpi-levels.json";
import timeFrames from "data/kpi-time-frames.json";
import quarters from "data/kpi-quarters.json";
import improvementDirections from "data/kpi-improvement-direction.json";
import TargetGoalTypeAutocomplete from "./TargetGoalTypeAutocomplete";
import BenchmarkGoalTypeAutocomplete from "./BenchmarkGoalTypeAutocomplete";
import {Checkbox} from "primereact/checkbox";
import KPITaskTable from "./KPITaskTable";
import KPITaskModal from "./KPITaskModal";
import {useListDepartmentsQuery} from "../../apis/departmentApi";
import * as yup from "yup";
import {classNames} from "primereact/utils";
import {useUserProfileQuery} from "../../apis/authenticationApi";
import { useResponsibleHiearchicalList, useAllDepartmentsHeads } from 'hooks/taskHooks';
import { useIsParentKpiInHiearchy } from 'hooks/kpiHooks';

const KPITargetModal = () => {
    const { target, task } = useParams();
    const selectedYear = useSelector(state => state.kpi.selectedYear);
    const {data: editTarget, isFetching} = useGetKpiTargetQuery(target);
    const [parent, setParent] = useState(editTarget?.parent);
    const {data: parentTarget, isLoading: isLoadingParent} = useGetKpiTargetQuery(parent || "new");
    const {data: kpiTargets, isFetching: isFetchingKpiTargets} = useListKpiTargetsQuery();
    const {data: departments, isFetching: isFetchingDepartments} = useListDepartmentsQuery();
    const [createKpiTarget] = useCreateKpiTargetMutation();
    const [updateKpiTarget] = useUpdateKpiTargetMutation();
    const {data: userProfile} = useUserProfileQuery();
    const isParentKpiInHiearchyCallback= useIsParentKpiInHiearchy();
    const isOwner =  userProfile?.company?.role === "CompanyOwner";
    const usersUnderSupervision = useResponsibleHiearchicalList(userProfile?.company?.account);
    const allDepartmentHeads = useAllDepartmentsHeads ();
    const responsibleParent = (kpiTargets || []).find(e => parentTarget?.id && e?.id === parentTarget.id)?.responsible;
    
    const canEdit = isOwner || (
        userProfile?.company?.role === "CompanyAdmin" && 
            editTarget?.level !== "Corporate" && (target === "new" || ((usersUnderSupervision || []).find(e => e.id !== userProfile?.company?.account && e.id === editTarget?.responsible)))
    );
    const goalHierarchyUsers = useResponsibleHiearchicalList(responsibleParent || userProfile?.company?.account, {excludeSameUserId: !isOwner});
    
    const possibleResponsibles = _.cloneDeep(((isOwner || !canEdit) && !responsibleParent) ? allDepartmentHeads : goalHierarchyUsers);
    const selectableParentKPIs = ((isOwner || !canEdit)? (kpiTargets || []) : 
                                            (kpiTargets || []).filter(e => usersUnderSupervision.find(u =>  u.id === e.responsible)))
                                .filter(e => !isParentKpiInHiearchyCallback(e, editTarget?.id));
    
    
    const schema = yup.object({
        name: yup.string().required("Please enter a name"),
        parent: isOwner ? undefined : yup.string().required("Please specify the parent KPI")
    });

    const handleSubmit = (data) => {
        const newData = _.cloneDeep(data);

        newData.deadline = newData.deadline.getTime();

        if(!data.id || data.id === 'new') {
            newData.year = selectedYear;

            createKpiTarget(newData);
        }
        else {
            updateKpiTarget(newData)
        }
    }

    const handleParentChange = (formik, event) => {
        setParent(event.value);
        const cloneTarget = _.cloneDeep(formik.values);
        cloneTarget.quarter = 'None';
        cloneTarget.timeFrame = 'None';
        formik.setValues(cloneTarget);
        formik.handleChange(event);
    }

    const handleGoalTypeChange = (formik, event) => {
        const cloneTarget = _.cloneDeep(formik.values);

        cloneTarget.type = event.value;

        if(event.value === 'Thematic') {
            cloneTarget.parent = undefined;
        }

        formik.setValues(cloneTarget);
    }

    const handleTargetChange = (formik, event) => {
        event.preventDefault();

        const cloneTarget = _.cloneDeep(formik.values);

        cloneTarget.name = event?.value?.name;
        cloneTarget.valueType = event?.value?.valueType;
        cloneTarget.improvementDirection = event?.value?.improvementDirection;

        formik.setValues(cloneTarget);
    }

    const handleQuarterChange = (formik, event) => {
        event.preventDefault();

        let quarter = -1;

        if(event.value === 'Q1') {
            quarter = 1;
        }
        else if(event.value === 'Q2') {
            quarter = 2;
        }
        else if(event.value === 'Q3') {
            quarter = 3;
        }
        else if(event.value === 'Q4') {
            quarter = 4;
        }

        const cloneTarget = _.cloneDeep(formik.values);

        if(quarter > 0) {
            cloneTarget.deadline = DateTime.now().set({year: selectedYear})
                .startOf('year')
                .plus({ months: (quarter - 1) * 3 })
                .endOf('quarter').toJSDate();
        }

        cloneTarget.quarter = event.value;
        cloneTarget.targets = [];

        formik.setValues(cloneTarget);
        formik.setFieldValue(`targets[${quarter - 1}]`, cloneTarget.targetValue, false);
    }

    const handleTimeFrameChange = (formik, event) => {
        event.preventDefault();

        let numberOfYears = 0;
        let quarter = -1;

        if(formik.values?.type === 'Task') {
            return;
        }

        if(event.value === 'TenYear') {
            numberOfYears = 9;
        }
        else if(event.value === 'FiveYear') {
            numberOfYears = 4;
        }
        else if(event.value === 'ThreeYear') {
            numberOfYears = 2;
        }
        else if(event.value === 'Quarter') {
            if(selectedYear === new Date().getFullYear()) {
                quarter = DateTime.now().quarter;
            }
            else {
                quarter = 1;
            }
        }

        const cloneTarget = _.cloneDeep(formik.values);

        if(quarter > 0) {
            cloneTarget.quarter = 'Q' + quarter;
            cloneTarget.deadline = DateTime.now().set({year: selectedYear})
                .startOf('year')
                .plus({ months: (quarter - 1) * 3 })
                .endOf('quarter').toJSDate();
        }
        else {
            cloneTarget.quarter = 'None';
            cloneTarget.deadline = DateTime.now().set({year: selectedYear}).plus({years: numberOfYears}).endOf('year').toJSDate();
        }

        cloneTarget.timeFrame = event.value;

        formik.setValues(cloneTarget);
    }

    const responsibleItemTemplate = (option) => {
        return option?.registered ? `${option?.firstName} ${option?.lastName}` : option?.email;
    }

    const responsibleValueTemplate = (option) => {
        const user = possibleResponsibles.find(user => user.id === option?.id);
        return user ? (user?.registered ? `${user?.firstName} ${user?.lastName}` : user?.email) : "Select Responsible";
    }

    const parentItemTemplate = (option) => {
        const timeFrame = timeFrames.find(frame => frame.id === option.timeFrame);

        return (timeFrame) ? `${option.name} (${timeFrame.value})` : option.name;
    }

    const timeFrameYearTarget = (timeFrame) => {
        timeFrame.displayValue = timeFrame.yearValue? timeFrame.value + " (" + ((parentTarget?.year || selectedYear) + timeFrame.yearValue)  +") ": timeFrame.value;
        return timeFrame;
    }

    if(isFetching || isFetchingKpiTargets || isLoadingParent || isFetchingDepartments) {
        return (<></>);
    }
    else {
        const clonedTarget = _.cloneDeep(editTarget);
        const possibleGoalTypes = _.cloneDeep(goalTypes).filter((goalType) => parentTarget?.type === 'None' || goalType.parent.includes(parentTarget?.type));
        const possibleLevels = _.cloneDeep(levels).filter((level) => (parentTarget?.level === 'None' || level.parent.includes(parentTarget?.level)) && (level.roles.includes(userProfile?.company?.role)) || !canEdit);
        const possibleTimeFrames = _.cloneDeep(timeFrames || []).filter((timeFrame) => parentTarget?.timeFrame === 'None'  || 
            (parentTarget?.timeFrame === "Quarter" ? timeFrame?.id === "Quarter" :
                ((parentTarget?.year ?? selectedYear) + (timeFrames.find(timeFrame => timeFrame.id === parentTarget?.timeFrame)?.yearValue ?? 0)) >= (selectedYear + (timeFrame?.yearValue ?? 0))
            )
        ).map(timeFrameYearTarget);
        const possibleQuarters = _.cloneDeep(quarters || []).filter((quarter) => parentTarget?.timeFrame !== "Quarter" || quarter.id === parentTarget?.quarter);
        const yearsLimit = (test) => (timeFrames || []).find((it) => it.id === test.timeFrame)?.yearValue ?? 1;
        const selectedTargets = _.cloneDeep(selectableParentKPIs || []).filter(test => test.id !== target && test.year <= selectedYear && (test.year + yearsLimit(test)) >= selectedYear);
        clonedTarget.deadline = clonedTarget.deadline < 1000 ? new Date() : new Date(clonedTarget.deadline);

        return (<>
            <FormModal width="75vw" type="KPI Target" editObject={clonedTarget || {name: ''}} handleSubmit={canEdit? handleSubmit : undefined} validationSchema={schema}
                renderForm={(formik, isFormFieldValid, getFormErrorMessage) =>
                    (<form onSubmit={formik.handleSubmit} className="formgrid grid">
                        <div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown className="w-4" value={formik.values?.type === 'None' ? undefined : formik.values?.type} name="type" options={possibleGoalTypes} onChange={(e) => handleGoalTypeChange(formik, e)} optionValue="id" optionLabel="value" placeholder="" disabled={!canEdit}/>
                                <label htmlFor="type">Type</label>
                            </span>
                        </div>
                        {formik.values?.type !== 'None' && formik.values?.type !== 'Target' && formik.values?.type !== 'Benchmark' && (<div className="field col-12">
                            <span className="p-float-label">
                                <InputText id="name" name="name" className="inputfield w-full" value={formik.values?.name} onChange={formik.handleChange} autoFocus disabled={!canEdit}/>
                                <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>Name</label>
                                {getFormErrorMessage('name')}
                            </span>
                        </div>)}
                        {formik.values?.type === 'Target' && (<div className="field col-12 flex">
                            <span className="p-float-label">
                                <TargetGoalTypeAutocomplete formik={formik} value={formik.values?.name} onChange={(e) => handleTargetChange(formik, e)} disabled={!canEdit}/>
                                <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>Name</label>
                                {getFormErrorMessage('name')}
                            </span>
                            <span className="p-float-label flex-grow-1">
                                <InputText id="notes" name="notes" className="inputfield w-full" value={formik.values?.notes} onChange={formik.handleChange} autoFocus disabled={!canEdit}/>
                                <label htmlFor="notes" className={classNames({ 'p-error': isFormFieldValid('notes') })}>Notes</label>
                                {getFormErrorMessage('notes')}
                            </span>
                        </div>)}
                        {formik.values?.type === 'Benchmark' && (<div className="field col-12 flex">
                            <span className="p-float-label">
                                <BenchmarkGoalTypeAutocomplete formik={formik} value={formik.values?.name} onChange={(e) => handleTargetChange(formik, e)} disabled={!canEdit}/>
                                <label htmlFor="name">Name</label>
                            </span>
                            <span className="p-float-label flex-grow-1">
                                <InputText id="notes" name="notes" className="inputfield w-full" value={formik.values?.notes} onChange={formik.handleChange} autoFocus disabled={!canEdit}/>
                                <label htmlFor="notes" className={classNames({ 'p-error': isFormFieldValid('notes') })}>Notes</label>
                                {getFormErrorMessage('notes')}
                            </span>
                        </div>)}
                        {(formik.values?.type === 'Target' || formik.values?.type === 'Benchmark') && (<div className="field flex col-12">
                            <div className="col-3 value-type-label">Value Type: </div>
                            <div className="col-3 value-type-value">{valueTypes.find(type => type.id === formik.values?.valueType)?.value}</div>
                            <div className="col-3 value-type-label">Optimal Improvement Direction: </div>
                            <div className="col-3 value-type-value">{improvementDirections.find(type => type.id === formik.values?.improvementDirection)?.value}</div>
                        </div>)}
                        {formik.values?.type !== 'None' && formik.values?.type !== 'Thematic' && (
                            <div className="field col-12">
                                <span className="p-float-label">
                                    <Dropdown className="w-4" value={formik.values?.parent === '' ? undefined : formik.values?.parent} itemTemplate={parentItemTemplate} name="parent" options={selectedTargets} onChange={(event) => handleParentChange(formik, event)} optionValue="id" optionLabel="name" showClear={true} placeholder="" disabled={!canEdit}/>
                                    <label htmlFor="parent" className={classNames({ 'p-error': isFormFieldValid('parent') })}>Parent</label>
                                    {getFormErrorMessage('parent')}
                                </span>
                            </div>
                        )}
                        {formik.values?.type !== 'None' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown className="w-4" value={formik.values?.level === 'None' ? undefined : formik.values?.level} name="level" options={possibleLevels} onChange={formik.handleChange} optionValue="id" optionLabel="value" placeholder="" disabled={!canEdit}/>
                                <label htmlFor="level">Level</label>
                            </span>
                        </div>)}
                        {formik.values?.type !== 'None' && formik.values?.level === 'Departmental' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown className="w-4" value={formik.values?.department === '' ? undefined : formik.values?.department} name="department" options={departments} onChange={formik.handleChange} optionValue="id" optionLabel="name" placeholder="" disabled={!canEdit}/>
                                <label htmlFor="department">Department</label>
                            </span>
                        </div>)}
                        {formik.values?.type !== 'None' && formik.values?.type !== 'Task' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown className="w-4" value={formik.values?.timeFrame === 'None' ? undefined : formik.values?.timeFrame} name="timeFrame" options={possibleTimeFrames} onChange={(e) => handleTimeFrameChange(formik, e)} optionValue="id" optionLabel="displayValue" placeholder="" disabled={!canEdit}/>
                                <label htmlFor="timeFrame">Time Frame</label>
                            </span>
                        </div>)}
                        {formik.values?.type !== 'None' && formik.values?.timeFrame === 'Quarter' && formik.values?.type !== 'Task' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown className="w-4" value={formik.values?.quarter === 'None' ? undefined : formik.values?.quarter} name="quarter" options={possibleQuarters} onChange={(e) => handleQuarterChange(formik, e)} optionValue="id" optionLabel="value" placeholder="" disabled={!canEdit}/>
                                <label htmlFor="quarter">Quarter</label>
                            </span>
                        </div>)}
                        {formik.values?.type !== 'None' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Dropdown name="responsible" id="responsible" className="w-4" value={formik.values?.responsible === '' ? undefined : formik.values?.responsible} options={possibleResponsibles} optionLabel="id" valueTemplate={responsibleValueTemplate} itemTemplate={responsibleItemTemplate} optionValue="id"
                                          onChange={formik.handleChange} placeholder="Select Responsible" disabled={!canEdit}/>
                                <label htmlFor="responsible">Responsible</label>
                            </span>
                        </div>)}
                        {formik.values?.type !== 'None' && (<div className="field col-12">
                            <span className="p-float-label">
                                <Calendar className="w-4" name="deadline" value={formik.values?.deadline} onChange={formik.handleChange} showIcon disabled={!canEdit || formik.values?.type !== 'Task'} />
                                <label htmlFor="deadline">Deadline</label>
                            </span>
                        </div>)}
                        {(formik.values?.type === 'Target' || formik.values?.type === 'Benchmark') && formik.values?.timeFrame === 'Annual' && <div className="field field-checkbox">
                            <Checkbox name="scorecard" checked={formik.values?.scorecard} onChange={formik.handleChange} disabled={!canEdit}/>
                            <label htmlFor="scorecard">Scoreboard Item</label>
                        </div>}
                        {formik.values?.type !== 'None' && (<><Divider />
                            <KPITargetAnnual formik={formik} disabled={!canEdit} />
                        </>)}
                        <div className="field col-12">
                            <KPITaskTable canAddTasks={canEdit} />
                        </div>
                    </form>)
                }>
            </FormModal>
            {task && (<KPITaskModal />)}</>)
    }
}

export default KPITargetModal;
